import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Link,
  IconButton,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import XIcon from "@mui/icons-material/X";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import MainLogo from "../../../../assets/images/icons/mainlogo.svg";
import MainLogoMobile from "../../../../assets/images/icons/mainlogo-mobile.png";
import DarkMainLogo from "../../../../assets/images/icons/mainlogoDark.svg";
import DarkMainLogoMobile from "../../../../assets/images/icons/mainlogoDark-mobile.png";
import UpArrowIcon from "../../../../assets/images/icons/upArrow.svg";

const WebFooter = () => {
  const theme = useTheme();
  const { t } = useTranslation("home");
  const [showScroll, setShowScroll] = useState(false);
  const isDark = theme.palette.mode === "dark";

  const navLinks = [
    { title: t("footer.terms"), href: "/terms-and-conditions" },
    { title: t("footer.privacy"), href: "/privacy-policy" },
    { title: t("footer.copyright"), href: "/copyright-policy" },
    { title: t("footer.linking"), href: "/hyperlink-policy" },
    { title: t("footer.faqs"), href: "/faqs" },
  ];

  const socialLinks = [
    {
      label: "X",
      href: "https://x.com/EduMinOfIndia?ref_src=twsrc%5Etfw",
      icon: <XIcon />,
    },
    // {
    //   label: "LinkedIn",
    //   href: "https://www.linkedin.com/company/ministry-of-education-india/",
    //   icon: <LinkedInIcon />,
    // },
    {
      label: "Facebook",
      href: "https://www.facebook.com/EduMinOfIndia/",
      icon: <FacebookIcon />,
    },
    {
      label: "YouTube",
      href: "https://www.youtube.com/channel/UCp2smQxAyu_09vXiPJ3vTTg",
      icon: <YouTubeIcon />,
    },
    {
      label: "Instagram",
      href: "https://www.instagram.com/eduminofindia/",
      icon: <InstagramIcon />,
    },
  ];

  useEffect(() => {
    const handleScroll = () => setShowScroll(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      component="footer"
      sx={{
        width: "100vw",
        maxWidth: "100vw",
        marginLeft: "calc(50% - 50vw)",
        bgcolor: isDark ? "#1a1d21" : "#fff",
        border: `1px solid ${isDark ? "#354039" : "#dcefdc"}`,
        borderBottom: 0,
        borderRadius: { xs: "34px 34px 0 0", md: "52px 52px 0 0" },
        overflow: "hidden",
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          minHeight: { xs: "auto", md: 160 },
          py: { xs: 4, md: 2.5 },
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "flex-start", lg: "center" },
          justifyContent: "space-between",
          gap: { xs: 3, lg: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 2, sm: 2.5 },
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <picture style={{ display: "contents" }}>
              <source
                media="(max-width: 767px)"
                srcSet={isDark ? DarkMainLogoMobile : MainLogoMobile}
              />
              <source
                media="(min-width: 768px)"
                srcSet={isDark ? DarkMainLogo : MainLogo}
              />
              <img
                src={isDark ? DarkMainLogo : MainLogo}
                alt="Government of India emblem"
                width="42"
                height="58"
              />
            </picture>
            <Typography
              sx={{
                color: isDark ? "#fff" : "#1b281b",
                fontSize: `calc(0.9rem * var(--dynamic-font-size, 1))`,
                fontWeight: 600,
                lineHeight: 1.45,
                marginLeft: { xs: "0.5rem", sm: "0.75rem" },
                whiteSpace: "nowrap",
              }}
            >
              Ministry of Education
              <br />
              <Box component="span" sx={{ fontWeight: 400 }}>
                Govt. of India
              </Box>
            </Typography>
          </Box> 

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              display: { xs: "none", sm: "block" },
              borderColor: isDark ? "#56605a" : "#9cac9f",
            }}
          />

          <Typography
            sx={{
              color: isDark ? "#fff" : "#1b281b",
              fontSize: `calc(0.9rem * var(--dynamic-font-size, 1))`,
              fontWeight: 700,
              lineHeight: 1.45,
              whiteSpace: "nowrap",
            }}
          >
            {t("footer.first_title")}
            <br />
            {t("footer.last_title")}
          </Typography>
        </Box>

        <Box
          aria-label="Ministry of Education social media"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {socialLinks.map((social) => (
            <IconButton
              key={social.label}
              component="a"
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Visit Ministry of Education on ${social.label}`}
              sx={{
                width: 34,
                height: 34,
                p: 0.75,
                color: "#fff",
                bgcolor: "#106426",
                "& svg": { fontSize: 20 },
                "&:hover, &:focus-visible": {
                  bgcolor: "#0b4d1c",
                  transform: "translateY(-2px)",
                },
                transition: "transform 160ms ease, background-color 160ms ease",
              }}
            >
              {social.icon}
            </IconButton>
          ))}
        </Box>

        <Box
          component="nav"
          aria-label="Footer navigation"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: { xs: "flex-start", lg: "flex-end" },
            columnGap: 2.25,
            rowGap: 1.25,
          }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.title}
              href={link.href}
              underline="none"
              sx={{
                position: "relative",
                pl: 1.5,
                color: isDark ? "#e3e8e4" : "#222",
                fontSize: `calc(0.8rem * var(--dynamic-font-size, 1))`,
                whiteSpace: "nowrap",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: "50%",
                  width: 5,
                  height: 5,
                  bgcolor: "currentColor",
                  borderRadius: "50%",
                  transform: "translateY(-50%)",
                },
                "&:hover, &:focus-visible": {
                  color: "#106426",
                  textDecoration: "underline",
                  textUnderlineOffset: "4px",
                },
              }}
            >
              {link.title}
            </Link>
          ))}
        </Box>
      </Container>

      <Box sx={{ bgcolor: "#273372", py: 2.25 }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#fff",
                opacity: 0.72,
                fontSize: `calc(0.75rem * var(--dynamic-font-size, 1))`,
              }}
            >
              {t("footer.allRights", { year: new Date().getFullYear() })}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#fff",
                opacity: 0.72,
                fontSize: `calc(0.75rem * var(--dynamic-font-size, 1))`,
              }}
            >
              {t("footer.lastUpdated")}
            </Typography>
          </Box>
        </Container>
      </Box>

      {showScroll && (
        <IconButton
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
          sx={{
            position: "fixed",
            right: { xs: 18, md: 40 },
            bottom: { xs: 18, md: 30 },
            zIndex: 999,
            width: 40,
            height: 40,
            p: 0,
            bgcolor: "transparent",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
            "&:hover, &:focus-visible": {
              bgcolor: "transparent",
              transform: "translateY(-2px)",
            },
            transition: "transform 160ms ease",
          }}
        >
          <img src={UpArrowIcon} alt="" width="40" height="40" />
        </IconButton>
      )}
    </Box>
  );
};

export default WebFooter;
